import React from 'react';

import type {
  LayerRegistration,
  MultiWidgetStateStore,
  PluginApp,
  TranslateFn,
  UserMenuOption,
} from '@typings/plugin-runtime.ts';
import MapNavButton from '@ui/buttons/MapNavButton.tsx';
import Tooltip from '@ui/tooltip';
import { isSurroundEnabled } from '@utils/configUtils.ts';
import { StateObjMultiple } from '@utils/stateObj.ts';

import { widgetEnum } from '../../../utils/constants.ts';
import type { PluginConfig } from '../config-schema.ts';
import UserMenu from './UserMenuView.tsx';

const USER_MENU_ID_DESKTOP = 'userMenuDesktop';
const USER_MENU_ID_MOBILE = 'userMenuMobile';

const langNames: Record<string, string> = {
  am: 'አማርኛ',
  ar: 'اَلْعَرَبِيَّةُ',
  de: 'Deutsch',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  hi: 'हिंदी',
  is: 'íslenska',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  nl: 'Nederlands',
  pl: 'Polski',
  pt: 'Português',
  so: 'Soomaali',
  'zh-Hans': '汉语',
  'zh-Hant': '漢語',
  'el-GR': 'Ελληνικά',
  ru: 'Русский',
  th: 'ไทย',
};

type VenueSummary = {
  id: string;
  locale: string;
  venueCode: string;
};

type VenueData = {
  id: string;
  venueList: Record<string, VenueSummary>;
};

type UserMenuState = {
  menuOptions: UserMenuOption[];
  isMobileMenuOpen: boolean;
  highContrastEnabled: boolean;
};

type MenuOptionOverride = Partial<UserMenuOption> & { id: string };

type AddMenuItemPayload = {
  iconId: string;
  id?: string;
  label: string;
  onClick: (option: UserMenuOption) => void | Promise<void | Partial<UserMenuOption> | false>;
};

const getPluginValue = <T,>(app: PluginApp, pluginKey: string, property: string): T | undefined => {
  const pluginConfig = app.config?.plugins?.[pluginKey];
  if (!pluginConfig) {
    return undefined;
  }

  return pluginConfig[property] as T | undefined;
};

function create(app: PluginApp, config: PluginConfig) {
  const T = app.gt() as TranslateFn;

  const surroundEnabled = isSurroundEnabled(
    (app.config?.plugins?.mapRenderer as Record<string, unknown> | undefined)?.surroundConfig,
  );

  const getMapIcon = (pitch: number) => (pitch === 0 ? '3d' : '2d');

  // High-contrast world view toggle. mapRenderer tracks the actual mode; this
  // menu reads the starting value from `surround/getHighContrast` in init() and
  // sends `surround/setHighContrast` on click to switch the map.
  const highContrastLabel = () =>
    widgetState.getState().highContrastEnabled ? T('userMenu:View Default Map') : T('userMenu:View High Contrast Map');
  // Filled folded-map = high contrast is available to enable; outline folded-map = active, switch back to default.
  const highContrastIcon = () => (widgetState.getState().highContrastEnabled ? 'map.outline' : 'map');
  const toggleHighContrast = () => {
    const highContrastEnabled = !widgetState.getState().highContrastEnabled;
    widgetState.update({ highContrastEnabled });
    app.bus.send('appInsights/log', {
      name: 'userMenu/highContrastToggleClick',
      properties: { pageLocation: 'sub menu', enabled: highContrastEnabled },
    });
    app.bus.send('surround/setHighContrast', { highContrast: highContrastEnabled });
    replaceOption({ id: 'highContrastToggler', label: highContrastLabel(), iconId: highContrastIcon() });
  };

  const options: UserMenuOption[] = [
    {
      iconId: getMapIcon(0),
      id: 'pitchToggler',
      label: T('userMenu:Switch Map View'),
      onClick: () => {
        app.bus.send('appInsights/log', {
          name: 'userMenu/toggleViewClick',
          properties: { pageLocation: 'sub menu' },
        });
        app.bus.send('pitchToggler/toggle');
      },
    },
  ];

  const widgetState: MultiWidgetStateStore<UserMenuState> = new StateObjMultiple<UserMenuState>({
    menuOptions: [...options],
    isMobileMenuOpen: false,
    highContrastEnabled: false,
  });

  const replaceOption = (option: MenuOptionOverride) => {
    const { menuOptions } = widgetState.getState();
    const optionIndex = menuOptions.findIndex(menuOption => option.id === menuOption.id);
    if (optionIndex >= 0) {
      menuOptions[optionIndex] = { ...menuOptions[optionIndex], ...option };
      widgetState.update({ menuOptions: [...menuOptions] });
    }
  };

  const pitchChanged = (pitch: number) => {
    replaceOption({
      id: 'pitchToggler',
      iconId: getMapIcon(pitch),
      label: pitch > 0 ? T('userMenu:View 2D Map') : T('userMenu:View 3D Map'),
    });
  };

  const handleResetMapClick = () => {
    app.bus.send('appInsights/log', {
      name: 'map/resetClick',
      properties: { pageLocation: 'map controls' },
    });
    app.bus.send('app/reset');
  };

  const handleMenuOpen = () => {
    app.bus.send('appInsights/log', {
      name: 'userMenu/openClick',
      properties: { pageLocation: app.env.isDesktop() ? 'map controls' : 'header' },
    });
  };

  const init = async () => {
    const { menuOptions } = widgetState.getState();

    if (!config.noLangOptions) {
      const venueData = await app.bus.get<undefined, VenueData>('venueData/getVenueData');
      const venueList = venueData.venueList;
      const myVenueCode = venueList[venueData.id]?.venueCode;

      Object.values(venueList)
        .filter(venue => venue.venueCode === myVenueCode)
        .filter(venue => venue.id !== venueData.id)
        .map(venue => ({
          lang: venue.locale,
          label: langNames[venue.locale] || venue.locale,
          vid: venue.id,
        }))
        .sort((left, right) => left.label.localeCompare(right.label))
        .forEach(venueOption => {
          menuOptions.unshift({
            iconId: 'language',
            id: `lang_${venueOption.lang}`,
            lang: venueOption.lang,
            label: venueOption.label,
            onClick: async () => {
              app.bus.send('appInsights/log', {
                name: 'userMenu/languageChangeClick',
                properties: { pageLocation: 'sub menu', lang: venueOption.lang },
              });
              const state = await app.bus.get<undefined, string>('deepLinking/getEncodedState');
              location.href = `${location.origin}${location.pathname}?lang=${venueOption.lang}&vid=${venueOption.vid}&s=${state}`;
            },
          });
        });
    }

    if ((app.config?.debug as Record<string, unknown> | undefined)?.darkModeOption) {
      menuOptions.push({
        iconId: 'reset',
        label: 'Toggle Dark Mode',
        onClick: () => {
          app.bus.send('appInsights/log', {
            name: 'userMenu/darkModeToggleClick',
            properties: { pageLocation: 'sub menu' },
          });
          app.bus.send('map/toggleDarkMode');
        },
      });
    }

    if (surroundEnabled) {
      // Read the current mode so the toggle starts pointing the right way.
      widgetState.update({
        highContrastEnabled: Boolean(await app.bus.get<undefined, boolean>('surround/getHighContrast')),
      });
      menuOptions.push({
        iconId: highContrastIcon(),
        id: 'highContrastToggler',
        label: highContrastLabel(),
        onClick: toggleHighContrast,
      });
    }

    const accessibilityUrl = getPluginValue<string>(app, 'userMenu', 'a11yUrl');
    if (accessibilityUrl) {
      menuOptions.push({
        iconId: 'accessibility',
        label: T('userMenu:Accessibility'),
        onClick: () => {
          app.bus.send('appInsights/log', {
            name: 'userMenu/a11yClick',
            properties: { pageLocation: 'sub menu' },
          });
          window.open(accessibilityUrl, '_blank');
        },
      });
    }

    if (app.config?.plugins?.['online/userFeedback'] !== undefined) {
      menuOptions.push({
        iconId: 'feedback',
        id: 'userFeedback',
        label: T('userFeedback:Feedback'),
        onClick: () => app.bus.send('userFeedback/open', { pageLocation: 'user menu' }),
      });
    }

    widgetState.update({ menuOptions });
  };

  const desktopLayer: LayerRegistration = {
    id: USER_MENU_ID_DESKTOP,
    widget: () => (
      <UserMenu isDesktop={app.env.isDesktop()} widgetState={widgetState} T={app.gt()} onOpen={handleMenuOpen} />
    ),
    show: true,
    isOverlay: true,
    layoutId: 'buttonOptions',
    widgetType: widgetEnum.Desktop,
    order: 50,
  };

  const mobileLayer: LayerRegistration = {
    id: USER_MENU_ID_MOBILE,
    widget: () => (
      <UserMenu isDesktop={app.env.isDesktop()} widgetState={widgetState} T={app.gt()} onOpen={handleMenuOpen} />
    ),
    isOverlay: true,
    layoutId: 'headerOnline',
    widgetType: widgetEnum.Mobile,
    shouldShow: currentlyShowing => currentlyShowing.includes('headerOnlineHome'),
  };

  app.bus.send('layers/register', {
    id: 'resetMapButtonDesktop',
    widget: () => (
      <Tooltip text={T('userMenu:Reset Map')} arrowPosition="left">
        <MapNavButton
          aria-label={T('userMenu:Reset Map')}
          iconName="reset"
          onClick={handleResetMapClick}
          data-testid="ResetMapButtonDesktop"
        />
      </Tooltip>
    ),
    show: true,
    isOverlay: true,
    layoutId: 'buttonOptions',
    widgetType: widgetEnum.Desktop,
    order: 25,
  });

  app.bus.send('layers/register', desktopLayer);
  app.bus.send('layers/register', mobileLayer);

  const addMenuItem = ({ iconId, id, label, onClick }: AddMenuItemPayload) => {
    const { menuOptions } = widgetState.getState();
    const option: UserMenuOption = {
      iconId,
      id,
      label,
      onClick: async currentOption => {
        const onClickReturn = await onClick(currentOption ?? option);
        if (onClickReturn) {
          replaceOption({ ...option, ...onClickReturn, id: (onClickReturn.id ?? option.id ?? '') as string });
        }
      },
    };
    menuOptions.push(option);
    widgetState.update({ menuOptions });
  };

  app.bus.on<{ pitch: number }>('map/moveEnd', ({ pitch }) => pitchChanged(pitch));
  app.bus.on<AddMenuItemPayload>('userMenu/addItem', addMenuItem);

  const runTest = async (initialState: Partial<UserMenuState>, testRoutine: () => Promise<void> | void) => {
    widgetState.update({ ...initialState });
    await testRoutine();
    return widgetState.getState();
  };

  return {
    init,
    runTest,
  };
}

export { create };
